import { useEffect, useRef, MouseEventHandler } from "react";
import { Link } from "react-router-dom";

type ButtonProps = {
  children: React.ReactNode;
  className?: string;
  rounded?: 'medium' | 'large' | 'none';
  width?: 'full' | 'fit';
  theme?: 'primary' | 'black' | 'danger' | 'outline-primary' | 'outline-danger';
  tag?: 'button' | 'link';
  size?: 'small' | 'medium' | 'big';
  type?: 'reset' | 'submit' | 'button';
  to?: string;
  disabled?: boolean;
  busy?: boolean;
  loading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

/**
 * Button component as button or link button
 * 
 * @param tag 'button' | 'link' -> default 'button'
 * @param type 'reset' | 'submit' | 'button' -> 'button', used only for button
 * @param to string: path to navigate, used only for link and required
 * @param rounded 'medium' | 'large' | 'none' -> 'medium'
 * @param themes 'primary' | 'black' | 'danger' | 'outline-primary' | 'outline-danger' -> default: 'primary'
 * @param width 'full' | 'fit' -> full
 * @param size 'small' (38px) | 'medium' (44px) | 'big' (48px) -> 'medium', measure in height
 * @param className string: css class
 * @param disabled boolean
 * @param busy boolean
 * @param loading boolean
 * @param onClick callback triggered when clicked
 */
const Button = ({
  children,
  className = '',
  tag = "button",
  rounded = "medium",
  theme = "primary",
  size = "medium",
  type = "button",
  width = 'full',
  to,
  disabled = false,
  busy = false,
  loading = false,
  onClick
}: ButtonProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    // remove focus
    if (disabled && linkRef.current) {
      linkRef.current.blur();
      linkRef.current.tabIndex = -1; 
    }
  }, [disabled])

  const paddingClass = size === "small" ? "py-[9px]" : // 9px
                    size === "medium" ? "py-3" : // 12px
                    "py-3.5"; // big 14px

  const baseClass = `${width === 'fit' ? 'w-fit' : 'w-full'} text-sm text-center
                    ${rounded === 'none' ? 'rounded-none' : rounded === "large" ? "rounded-[19px]" : "rounded-md"}`;

  let themeClass: string;
  switch (theme) {
    case "black":
      themeClass = `text-white bg-[#2B2B2E] hover:bg-stone-900
      disabled:bg-stone-500 aria-disabled:bg-stone-500 aria-busy:bg-stone-500
      focus-visible:outline-none focus-visible:ring focus-visible:ring-slate-500`;
      break;
    case "primary":
      themeClass = `text-white bg-primary hover:bg-secondary
      disabled:bg-indigo-300 aria-disabled:bg-indigo-300 aria-busy:bg-indigo-300
      focus-visible:outline-none focus-visible:ring focus-visible:ring-accent`;
      break;
    case "danger":
      themeClass = `text-white bg-red-500 hover:bg-red-700
      disabled:bg-red-300 aria-disabled:bg-red-300 aria-busy:bg-red-300
      focus-visible:outline-none focus-visible:ring focus-visible:ring-red-200`;
      break;
    case "outline-primary":
      themeClass = `text-shadow leading-[18px] bg-transparent hover:text-white hover:bg-primary
      border border-gray
      disabled:text-izygray aria-disabled:text-izygray aria-busy:text-izygray
      disabled:border-izygray aria-disabled:border-izygray aria-busy:border-izygray
      focus-visible:outline-none focus-visible:ring focus-visible:ring-primary`;
      break;
    case "outline-danger":
      themeClass = `text-red-600 leading-[18px] bg-transparent hover:text-white hover:bg-red-500
      border border-red-500
      disabled:text-red-200 aria-disabled:text-red-200 aria-busy:text-red-200
      disabled:border-red-200 aria-disabled:border-red-200 aria-busy:border-red-200
      disabled:hover:bg-transparent
      focus-visible:outline-none focus-visible:ring focus-visible:ring-red-400`;
      break;
  }

  const renderContent = () => (
    <>
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span>Loading...</span>
        </span>
      ) : (
        children
      )}
    </>
  );

  if (tag === "link") {    
    if (!to) {
      throw new Error("Attribute 'to' is required");
    }
    
    return (
      <Link
        ref={linkRef}
        to={to}
        aria-disabled={disabled || loading}
        className={`cursor-pointer aria-disabled:pointer-events-none
        ${baseClass} ${paddingClass} ${themeClass} ${className}`}>
        {renderContent()}
      </Link>
    )
  }

  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={busy || loading}
      className={`cursor-pointer aria-busy:cursor-wait disabled:cursor-not-allowed
        ${baseClass} ${paddingClass} ${themeClass} ${className}`}
      onClick={(e) => {onClick && onClick(e)}}>
      {renderContent()}
    </button>
  )
}

export default Button;